import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
// import { useAuth } from "../hooks/chat/useAuth";
import { storage } from '../lib/storage';
import { refreshProfile } from '../utils/profile';
import { CreditPurchaseDialog } from './credits/CreditPurchaseDialog';
import { NavigationControls } from './header/NavigationControls';
import { WalletInfo } from './header/WalletInfo';
import { ProfileMenu } from './header/ProfileMenu';
import { HeaderActions } from './header/HeaderActions';

interface HeaderProps {
  toggleSidebar: () => void;
  theme: 'light' | 'dark' | 'system';
  toggleTheme: () => void;
  getThemeIcon: () => React.ComponentType;
  getThemeText: () => string;
  onSignOut: () => void;
  sidebarOpen: boolean;
  onNewChat: () => void;
  hasNewChat: boolean;
  onCreditUpdate?: (handler: (credit: number) => void) => void;
}

export const Header: React.FC<HeaderProps> = ({
  toggleSidebar,
  theme,
  toggleTheme,
  getThemeIcon,
  getThemeText,
  onSignOut,
  sidebarOpen,
  onNewChat,
  hasNewChat,
  onCreditUpdate
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const { state: { user } } = useAuth();
  const [profile, setProfile] = useState<any>(null);

  React.useEffect(() => {
    const loadProfile = async () => {
      if (user?.address) {
        const profileData = await storage.getProfile(user.address);
        setProfile(profileData);
      }
    };
    loadProfile();
  }, [user]);

  // Register credit update handler with parent
  React.useEffect(() => {
    if (onCreditUpdate) {
      onCreditUpdate((newCredit: number) => {
        setProfile((prev: any) => prev ? { ...prev, credit: newCredit } : prev);
      });
    }
    
    // Listen for credit update events from credit purchases
    const handleCreditUpdateEvent = (event: CustomEvent) => {
      const { credit } = event.detail;
      console.log('Credit update event received:', credit);
      setProfile((prev: any) => prev ? { ...prev, credit } : prev);
    };
    
    window.addEventListener('creditUpdate', handleCreditUpdateEvent as EventListener);
    
    return () => {
      window.removeEventListener('creditUpdate', handleCreditUpdateEvent as EventListener);
    };
  }, [onCreditUpdate]);

  const handlePurchaseCredits = () => {
    setShowPurchaseDialog(false);
  };

  if (!user) return null;

  const nfdName = localStorage.getItem('nfdName');
  const balance = Number(localStorage.getItem('walletBalance') || '0');
  const minBalance = Number(localStorage.getItem('walletMinBalance') || '0');
  const rekeyTo = localStorage.getItem('walletRekeyTo');
  const credits = profile?.credit || 0;

  return (
    <div className="h-16 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-between px-4 md:px-6">
      <NavigationControls
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        onNewChat={onNewChat}
        hasNewChat={hasNewChat}
      />

      <div className="flex items-center space-x-2 md:space-x-4">
        <div className="relative">
          <WalletInfo
            address={user.address}
            nfdName={nfdName}
            balance={balance}
            minBalance={minBalance}
            rekeyTo={rekeyTo}
            credits={credits}
            profile={profile}
            onClick={() => setShowUserMenu(!showUserMenu)}
          />

          <ProfileMenu
            showUserMenu={showUserMenu}
            setShowUserMenu={setShowUserMenu}
            onSignOut={onSignOut}
            displayName={nfdName || `${user.address.slice(0, 4)}...${user.address.slice(-4)}`}
            credits={credits}
            theme={theme}
            toggleTheme={toggleTheme}
            getThemeIcon={getThemeIcon}
            getThemeText={getThemeText}
            onPurchaseCredits={() => setShowPurchaseDialog(true)}
            walletAddress={user.address}
            nfdName={nfdName}
            balance={balance}
            minBalance={minBalance}
            rekeyTo={rekeyTo}
            profile={profile}
          />
        </div>
      </div>

      <CreditPurchaseDialog
        isOpen={showPurchaseDialog}
        onClose={() => setShowPurchaseDialog(false)}
      />
    </div>
  );
};
